import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import NavBar from '../../components/NavBar/NavBar';
import Footer from '../../components/Footer/Footer';

// Import your project's design tokens if available
import '../../styles/confirmation.css';

const confirmationTypes = {
  appointment: {
    title: 'Appointment Scheduled!',
    message: 'Thank you for scheduling an appointment with us. We will get back to you shortly with the details of your appointment and next steps.',
    icon: (
      <svg className="confirmation-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 2V5" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M16 2V5" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M3.5 9.09H20.5" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M19 9.5V16.5C19 20 18 22 15 22H9C6 22 5 20 5 16.5V9.5C5 6 6 4 9 4H15C18 4 19 6 19 9.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M12 14.2H12.01M8.79999 14.2H8.80999M8.79999 17H15.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  quote: {
    title: 'Quote Request Received!',
    message: 'Thank you for your interest in our services. We have received your request for a quote and will send you detailed pricing information via email shortly.',
    icon: (
      <svg className="confirmation-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 22C17.5 22 22 17.5 22 12C22 6.5 17.5 2 12 2C6.5 2 2 6.5 2 12C2 17.5 6.5 22 12 22Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M7.75 11.9999L10.58 14.8299L16.25 9.16992" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  signup: {
    title: 'Sign-up Confirmed!',
    message: 'Thank you for signing up! Our representative will reach out to you shortly to discuss the next steps and get you started with our services.',
    icon: (
      <svg className="confirmation-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
  contact: {
    title: 'Message Received!',
    message: 'Thank you for contacting us. We have received your message and a representative will get back to you shortly.',
    icon: (
      <svg className="confirmation-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="currentColor" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
    ),
  },
};

const ConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(30);
  const searchParams = new URLSearchParams(location.search);
  const type = searchParams.get('type') || 'contact';
  
  // Get additional parameters
  const name = searchParams.get('name') || '';
  const email = searchParams.get('email') || '';
  const customMessage = searchParams.get('message');
  
  const { title, message, icon } = confirmationTypes[type] || confirmationTypes.contact;
  
  // Format the message with dynamic values
  const formattedMessage = customMessage || message;
  const personalizedMessage = name 
    ? formattedMessage.replace(/\b(?:user|client|customer)\b/gi, name)
    : formattedMessage;

  useEffect(() => {
    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Clean up on unmount
    return () => clearInterval(timer);
  }, [navigate]);

  return (
    <div className="confirmation-page">
      <NavBar />
      <main className="confirmation-container">
        <div className="confirmation-card">
          <div className="confirmation-icon-container">
            {icon}
          </div>
          <h1 className="confirmation-title">{title}</h1>
          
          <div className="confirmation-content">
            <p className="confirmation-message">{personalizedMessage}</p>
            
            {email && (
              <div className="confirmation-details">
                <p>We've sent a confirmation to <strong>{email}</strong></p>
              </div>
            )}
            
            <div className="confirmation-countdown">
              <p>Redirecting in <span className="countdown-number">{countdown}</span> seconds...</p>
              <p className="confirmation-link">
                <a href="/" className="text-link">
                  Or click here to return home immediately
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ConfirmationPage;
